<script setup lang="ts">
import {
  type DefaultInputProps,
  type IFileState,
  type IPixabayImage,
} from "@/interfaces";
import InputWrapper from "@/stories/form/InputWrapper/InputWrapper.vue";
import { nanoid } from "nanoid";
import { FileAcceptTypes, FileSizes } from "@/enums";
import { computed, ref, toRefs } from "vue";
import helpers from "@/helpers";
import FlexRow from "@/stories/layout/FlexRow/FlexRow.vue";
import FileGallery from "@/stories/displays/FileGallery/FileGallery.vue";
import getFileCategory from "@/helpers/files/getFileCategory";
import { type FileCategory } from "@/types";
import { useI18n } from "vue-i18n";
import { useFieldValidationError } from "@/composables/useFieldValidationError";
import FileTrigger from "@/stories/form/FileTrigger/FileTrigger.vue";
import ImageTrigger from "@/stories/form/ImageTrigger/ImageTrigger.vue";
import HalfFormGroup from "@/stories/form/HalfFormGroup/HalfFormGroup.vue";
import PixabayDialog from "@/stories/dialogs/PixabayDialog.vue";
import { MAX_FILE_SIZE_TEXTS } from "@/consts";
import ImageEditorDialog from "@/stories/dialogs/ImageEditorDialog.vue";
import LoadingDialog from "@/stories/dialogs/LoadingDialog.vue";
import { sentryException } from "@/sentry";
import { simpleError } from "@/stories/displays/SimpleError/SimpleError.vue";
import { usePasteImage } from "@/composables/usePasteImage";

const id = nanoid();

interface Props extends Omit<DefaultInputProps, "label"> {
  modelValue: IFileState[] | undefined;
  maxFile: number;
  label?: string;
  accept: FileAcceptTypes;
  maxSize?: FileSizes;
  pixabaySupport?: boolean;
  autoHideTrigger?: boolean;
  /**
   * You can enable this feature to replace the selected images completely.
   */
  autoReplace?: boolean;
  nameEditing?: boolean;
  favorite?: boolean;
  isHalfFormGroupActive?: boolean;
  showFavoriteIcon?: boolean;
  imageGalleryEnabled?: boolean;
  /**
   * You can hide the trigger whenever you want. You can use this feature to
   * allow users select only 1 image.
   */
  hideTrigger?: boolean;
  allowPasteImage?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  maxSize: FileSizes.Max15MB,
  pixabaySupport: false,
  autoHideTrigger: false,
  autoReplace: false,
  nameEditing: false,
  favorite: false,
  variant: "file",
  isHalfFormGroupActive: true,
  showFavoriteIcon: true,
  imageGalleryEnabled: true,
  hideTrigger: false,
  allowPasteImage: false,
});

const emit = defineEmits<{
  "update:modelValue": [IFileState[] | undefined];
}>();

const {
  accept,
  maxSize,
  maxFile,
  modelValue,
  errorMessageKey,
  autoHideTrigger,
  autoReplace,
  validation,
  name,
  favorite,
  hideTrigger,
  allowPasteImage,
} = toRefs(props);

const { t } = useI18n();
const selectionError = ref<string>();
const showPixabayDialog = ref(false);
const croppingImageSrc = ref<string>();
const cropppingFile = ref<IFileState>();
const loading = ref(false);

const isTypesValid = (type: FileCategory, files: FileCategory[]) => {
  const matched = files.filter((item) => item === type);
  return matched.length === files.length;
};

const isFilesAcceptable = (types: FileCategory[]): boolean => {
  if (
    accept.value === FileAcceptTypes.OnlyImage &&
    !isTypesValid("image", types)
  ) {
    return false;
  }

  if (accept.value === FileAcceptTypes.OnlyPDF && !isTypesValid("pdf", types)) {
    return false;
  }

  if (
    accept.value === FileAcceptTypes.OnlyDocument &&
    types.includes("image")
  ) {
    return false;
  }

  return true;
};

const updateTheModel = (newFiles: IFileState[]) => {
  let totalCount = [...(modelValue?.value || []), ...newFiles].filter(
    (item) => item.action !== "delete",
  ).length;

  // If the auto replace is enable, we should check for only new files
  if (autoReplace.value) {
    totalCount = newFiles.length;
  }

  // Check total file count
  if (totalCount > maxFile.value) {
    selectionError.value = t("global.file_count_error", {
      count: maxFile.value,
    });
    return;
  }

  // There can be only 1 favorite file if favorite feauture is enabled.
  if (favorite.value) {
    const hasFavorite = (modelValue.value || []).some(
      (item) => item.action !== "delete" && item.isFavorite,
    );
    if (hasFavorite === false && newFiles.length && newFiles[0]) {
      newFiles[0].isFavorite = true;
    }
  }

  // You can set the update the state completely.
  if (autoReplace.value) {
    if (favorite.value && newFiles[0]) {
      newFiles[0].isFavorite = true;
    }
    emit("update:modelValue", newFiles);
  } else {
    // Update the state
    const newState: IFileState[] = [...(modelValue.value || []), ...newFiles];
    emit("update:modelValue", newState);
  }

  if (validation?.value?.errors) {
    delete validation.value.errors[name.value];
  }
};

const onFileSelection = async (fileList: FileList | null) => {
  // Let's clear the errors
  selectionError.value = undefined;

  if (!fileList) {
    return;
  }

  // Converting array and create a new state
  const files: File[] = Array.from(fileList);
  // Check the maximum file size
  const unallowedSize = files.some((file) => file.size > maxSize.value);
  if (unallowedSize) {
    selectionError.value = t("global.file_size_error", {
      size: MAX_FILE_SIZE_TEXTS[maxSize.value || FileSizes.Max15MB],
    });
    return;
  }

  const fileTypes = files.map(getFileCategory);
  if (isFilesAcceptable(fileTypes) === false) {
    selectionError.value = t("global.file_type_error");
    return;
  }

  const newFiles = await convertFiles(fileList);

  updateTheModel(newFiles);
};

const handlePixabaySelection = async (files: IPixabayImage[]) => {
  showPixabayDialog.value = false;

  // Let's clear the errors
  selectionError.value = undefined;

  if (!files.length) {
    return;
  }

  // Check the maximum file size
  const unallowedSize = files.some((file) => file.imageSize > maxSize.value);
  if (unallowedSize) {
    selectionError.value = t("global.file_size_error", {
      size: MAX_FILE_SIZE_TEXTS[maxSize.value || FileSizes.Max15MB],
    });
    return;
  }

  const newFiles: IFileState[] = files.map((file) => {
    return {
      name: file.id.toString(),
      originalName: file.id.toString(),
      size: file.imageSize,
      base64: "",
      url: file.largeImageURL,
      type: "image",
      action: "upload",
    };
  });

  updateTheModel(newFiles);
};

const currentErrorMessage = computed(() => {
  return selectionError.value || errorMessageKey.value;
});

const convertFiles = async (files: FileList | null): Promise<IFileState[]> => {
  if (!files) {
    return [];
  }

  const items: IFileState[] = [];
  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    if (!file) {
      continue;
    }

    items.push({
      name: file.name,
      originalName: file.name,
      size: file.size,
      base64: await helpers.files.toBase64(file),
      type: getFileCategory(file),
      action: "upload",
      file: file,
    });
  }

  return items;
};

const handleDelete = (file: IFileState) => {
  if (file.action === "upload") {
    const newState = (modelValue.value || []).filter(
      (item) =>
        (file.base64 && item.base64 !== file.base64) ||
        (file.url && item.url !== file.url),
    );
    emit("update:modelValue", newState);
  } else {
    // This file is marked to delete
    file.action = "delete";

    // If the file is the favorite file, we should mark the next item as the
    // favorite file, if setting favorite is allowed, and there is a file.
    if (favorite.value && file.isFavorite) {
      const first = (modelValue.value || [])
        .filter((item) => item.action !== "delete")
        .at(0);
      if (first) {
        first.isFavorite = true;
      }
    }
  }
};

const handleFavorite = (file: IFileState) => {
  const newState = (modelValue.value || []).map((item) => {
    const hasBase64 = file.base64 && file.base64.trim().length > 0;
    const hasURL = file.url && file.url.trim().length > 0;

    if (
      (hasBase64 && item.base64 === file.base64) ||
      (hasURL && item.url === file.url)
    ) {
      item.isFavorite = true;
    } else {
      item.isFavorite = undefined;
    }
    return item;
  });
  emit("update:modelValue", newState);
};

const imageTriggedEnabled = computed(() => {
  return [FileAcceptTypes.ImageOrDocument, FileAcceptTypes.OnlyImage].includes(
    accept.value,
  );
});

const isTriggerEnable = computed(() => {
  if (hideTrigger.value) {
    return false;
  }

  if (autoHideTrigger.value) {
    const visibleFiles = (modelValue.value || []).filter(
      (item) => item.action !== "delete",
    );
    return visibleFiles.length < maxFile.value;
  }

  return true;
});

const onFileInputClick = () => {
  const input = document.createElement("input");
  input.type = "file";

  if (accept.value) {
    input.accept = accept.value;
  }

  input.multiple = maxSize.value > 1;

  input.onchange = async function () {
    onFileSelection(input.files);
  };
  input.click();
};

const DOCUMENT_ICONS: Record<FileAcceptTypes, string> = {
  [FileAcceptTypes.OnlyPDF]: "far fa-file-pdf",
  [FileAcceptTypes.OnlyImage]: "far fa-image",
  [FileAcceptTypes.OnlyDocument]: "far fa-files",
  [FileAcceptTypes.ImageOrDocument]: "far fa-files",
};

const { hasError } = useFieldValidationError(name, validation);

const openPixabayDialog = () => {
  showPixabayDialog.value = true;
};

const currentFileCount = computed(() => {
  return (modelValue.value || []).filter((file) => file.action !== "delete")
    .length;
});

const imageUrlToBase64 = async (url: string): Promise<string | undefined> => {
  const response = await fetch(url, {
    credentials: "same-origin",
  });
  const blob = await response.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result?.toString());
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const handleEditClick = async (file: IFileState) => {
  const url = file.url || file.base64;
  if (!url) {
    return;
  }

  try {
    loading.value = true;
    cropppingFile.value = file;
    const base64 = await imageUrlToBase64(url);
    if (base64) {
      croppingImageSrc.value = base64;
    }
  } catch (error) {
    sentryException(error);
    simpleError("global.error_api");
  } finally {
    loading.value = false;
  }
};

const handleCropApply = (croppedBase64?: string) => {
  croppingImageSrc.value = undefined;

  if (!croppedBase64 || !cropppingFile.value) {
    return;
  }

  // We can update it directly because it's an object reference.
  cropppingFile.value.id = undefined; // Should be a new file
  cropppingFile.value.base64 = croppedBase64; // Cropped data
  cropppingFile.value.action = "upload"; // Should be uploaded
  // We should clear it incase of this is a pixabay image
  cropppingFile.value.url = undefined;
  // If user is cropped the image, it's not the deault image anymore
  cropppingFile.value.isDefault = false;
};

const maxPixabaySelectionCount = computed(() => {
  if (autoReplace.value) {
    return 1;
  }

  return maxFile.value - currentFileCount.value;
});

usePasteImage({
  enabled: allowPasteImage.value,
  onPaste: onFileSelection,
});
</script>

<template>
  <FlexRow>
    <InputWrapper
      :name="name"
      :forId="id"
      :label="label"
      :description="description"
      :isRequired="isRequired"
      :inputKey="inputKey"
      :tooltip="tooltip"
      :disabled="disabled"
    >
      <div class="flex flex-col gap-4">
        <FileGallery
          v-if="imageGalleryEnabled && modelValue && modelValue.length > 0"
          :files="modelValue"
          :nameEditing="nameEditing"
          :validation="validation"
          :name="name"
          :favorite="favorite"
          :showFavoriteIcon="showFavoriteIcon"
          @delete="handleDelete"
          @favorite="handleFavorite"
          @edit="handleEditClick"
        />

        <div v-if="isTriggerEnable || currentFileCount < maxFile">
          <div v-if="isTriggerEnable" class="mb-2">
            <HalfFormGroup
              v-if="imageTriggedEnabled"
              :isActive="isHalfFormGroupActive"
            >
              <ImageTrigger
                :name="name"
                :error="!!hasError"
                :validation="validation"
                :pixabaySupport="pixabaySupport"
                @clickFromComputer="onFileInputClick"
                @dropped="onFileSelection"
                @clickFromCloud="openPixabayDialog"
              />
            </HalfFormGroup>
            <FileTrigger
              v-else
              :icon="DOCUMENT_ICONS[accept]"
              :disabled="disabled"
              :accept="accept"
              :maxSize="maxSize"
              @click="onFileInputClick"
              @dropped="onFileSelection"
            />
          </div>

          <div v-if="currentErrorMessage" class="text-red-500">
            {{ $t(currentErrorMessage) }}
          </div>
        </div>
      </div>
    </InputWrapper>
  </FlexRow>

  <ImageEditorDialog
    v-if="croppingImageSrc"
    :src="croppingImageSrc"
    @close="croppingImageSrc = undefined"
    @apply="handleCropApply"
  />

  <PixabayDialog
    v-if="showPixabayDialog"
    :max="maxPixabaySelectionCount"
    @close="showPixabayDialog = false"
    @select="handlePixabaySelection"
  />

  <LoadingDialog v-if="loading" />
</template>
